import { consume } from '@lit/context'
import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { SchmancyTabsKeepMountedContext, SchmancyTabsModeContext } from './context'

@customElement('schmancy-tab')
export default class SchmancyTab extends SchmancyElement {
	// A kept-mounted panel hides with `visibility`, and every other way of
	// hiding it was measured against the thing it is here to protect. Emptying
	// the panel (what an unkept one does) and `display: none` and
	// `content-visibility: hidden` all take the box away, so a child that sizes
	// itself from what it measures — a document viewer fitting a page to its
	// width — is told it is 0 wide and rebuilds against that, twice: once
	// leaving, once returning. `visibility: hidden` leaves the box laid out at
	// its real size, so nothing is measured that was not true, while still
	// dropping the panel out of the accessibility tree and out of the focus
	// order the way an inactive panel must.
	//
	// Panels overlap rather than stack because they are all live at once: each
	// takes the group's single content cell, so the container holds one panel's
	// geometry however many are mounted, and switching moves nothing.
	static styles = [
		css`
			:host([keep-mounted]) {
				grid-row: 1;
				grid-column: 1;
			}
			:host([keep-mounted]:not([active])) {
				visibility: hidden;
			}
		`,
	]

	@property({ type: String, reflect: true }) label
	@property({ type: String, reflect: true }) value
	@property({ type: Boolean, reflect: true }) active = false

	/** How many things in this tab are failing: ✕N in the error ink beside the label. None at 0. */
	@property({ type: Number }) failing = 0

	/** What a screen reader hears for `failing`, in the host's words. The glyph and count alone say little. */
	@property({ attribute: 'failing-label' }) failingLabel = ''

	@consume({ context: SchmancyTabsModeContext, subscribe: true })
	@state()
	mode

	/**
	 * Set by the enclosing group, never by the consumer. Reflected because the
	 * `:host` rules above are what actually hide the panel.
	 */
	@consume({ context: SchmancyTabsKeepMountedContext, subscribe: true })
	@property({ type: Boolean, reflect: true, attribute: 'keep-mounted' })
	keepMounted = false

	/**
	 * True while the group has taken this panel's content away — the panel that
	 * is not being shown, in a group that does not keep its panels mounted.
	 * Virtualizers need real layout rather than a hidden box, which is why the
	 * content goes rather than being hidden in place.
	 */
	@state() private emptied = false

	/** A tab's first render is what the group's slotchange reads; only a label or
	 *  value changed after that needs announcing. */
	private relabelled = false

	protected willUpdate(changed: Map<PropertyKey, unknown>): void {
		super.willUpdate(changed)
		this.relabelled = this.hasUpdated && (changed.has('label') || changed.has('value') || changed.has('failing') || changed.has('failingLabel'))
		this.emptied = this.mode === 'tabs' && !this.active && !this.keepMounted
		// The panel keeps its tabpanel role and its own name while emptied, so
		// without this a screen reader meets one named panel per tab and every
		// one but the shown panel is empty. A panel that renders nothing
		// announces nothing.
		if (this.emptied) this.setAttribute('aria-hidden', 'true')
		else this.removeAttribute('aria-hidden')
	}

	protected updated(changedProperties: Map<PropertyKey, unknown>) {
		if (changedProperties.has('active') && this.active) {
			// Trigger resize for virtualizers to recalculate when tab becomes visible
			requestAnimationFrame(() => {
				window.dispatchEvent(new Event('resize'))
			})
		}
		// The group builds its strip from every tab's label and value, and nothing
		// else tells it that one changed after this tab was slotted.
		if (this.relabelled) this.dispatchEvent(new CustomEvent('schmancy-tab-relabel', { bubbles: true }))
	}

	protected render(): unknown {
		if (this.emptied) {
			return html``
		}
		return html`<slot></slot>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-tab': SchmancyTab
	}
}
